/**
 * On-device speech recognition, behind one interface.
 *
 * Two backends, because the RN app runs in two places and they have nothing in
 * common under the hood:
 *
 *   native  `expo-speech-recognition`, which wraps SFSpeechRecognizer on iOS
 *           and android.speech.SpeechRecognizer on Android. With
 *           `requiresOnDeviceRecognition` the audio never leaves the handset —
 *           no per-minute cost, and it still works in a basement apartment with
 *           one bar. This is the backend that makes variation 1 worth having.
 *
 *   web     The browser's SpeechRecognition. Present so the feature can be
 *           driven and demoed in the Expo web build. Note it is NOT on-device
 *           in Chrome — Chrome ships the audio to Google — so the web path has
 *           the privacy properties of a cloud recogniser even though the native
 *           one does not. Do not quote web numbers as on-device numbers.
 *
 * The module is deliberately import-safe on both platforms: the native package
 * is required lazily, so the web bundle never tries to resolve it and a build
 * without the native module installed degrades to `isAvailable() === false`
 * rather than crashing the medication hub on import.
 */
import { Platform } from "react-native";

export interface SpeechRecognitionHandle {
    stop: () => void;
    abort: () => void;
}

export interface SpeechRecognitionCallbacks {
    /** Fires as she talks. `isFinal` marks a phrase the recogniser has committed to. */
    onResult: (transcript: string, isFinal: boolean) => void;
    onError: (message: string) => void;
    onEnd: () => void;
}

const IS_WEB = Platform.OS === "web";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function loadNativeModule(): any | null {
    if (IS_WEB) {
        return null;
    }
    try {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        return require("expo-speech-recognition");
    } catch {
        // Not in this build (the module needs a dev build, not Expo Go).
        return null;
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function webSpeechCtor(): any | null {
    if (!IS_WEB || typeof window === "undefined") {
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function isSpeechRecognitionAvailable(): boolean {
    return IS_WEB ? webSpeechCtor() !== null : loadNativeModule() !== null;
}

/** True only when the recogniser genuinely runs on the handset. */
export function isOnDevice(): boolean {
    return !IS_WEB && loadNativeModule() !== null;
}

export async function requestSpeechPermission(): Promise<boolean> {
    if (IS_WEB) {
        // The browser prompts on start(); there is nothing to request up front.
        return webSpeechCtor() !== null;
    }
    const mod = loadNativeModule();
    if (!mod) {
        return false;
    }
    const result = await mod.ExpoSpeechRecognitionModule.requestPermissionsAsync();
    return Boolean(result?.granted);
}

/**
 * Start recognising. Returns a handle, or throws if no backend exists.
 *
 * `contextualStrings` is the single highest-leverage argument here: drug names
 * are exactly what a general dictation model gets wrong, and passing the
 * patient's already-known medications plus common drug names measurably moves
 * "atorvastatin" out of the "a torva statin" bucket.
 */
export async function startSpeechRecognition(
    callbacks: SpeechRecognitionCallbacks,
    options: { contextualStrings?: string[] } = {}
): Promise<SpeechRecognitionHandle> {
    if (IS_WEB) {
        const Ctor = webSpeechCtor();
        if (!Ctor) {
            throw new Error("This browser has no speech recognition");
        }
        const recognition = new Ctor();
        recognition.lang = "en-US";
        recognition.continuous = true;
        recognition.interimResults = true;

        // The browser API hands back a growing result list, not a growing
        // string, so the full transcript has to be rebuilt each event.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        recognition.onresult = (event: any) => {
            let finalText = "";
            let interimText = "";
            for (let i = 0; i < event.results.length; i++) {
                const result = event.results[i];
                const text = result[0]?.transcript ?? "";
                if (result.isFinal) {
                    finalText += text;
                } else {
                    interimText += text;
                }
            }
            if (finalText) {
                callbacks.onResult(finalText.trim(), true);
            }
            if (interimText) {
                callbacks.onResult((finalText + interimText).trim(), false);
            }
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        recognition.onerror = (event: any) => {
            // "no-speech" fires on any quiet stretch — she is walking to the
            // next room, not finished — so it is not surfaced as an error.
            if (event?.error === "no-speech" || event?.error === "aborted") {
                return;
            }
            callbacks.onError(String(event?.error ?? "speech recognition failed"));
        };
        recognition.onend = () => callbacks.onEnd();
        recognition.start();
        return {
            stop: () => recognition.stop(),
            abort: () => recognition.abort()
        };
    }

    const mod = loadNativeModule();
    if (!mod) {
        throw new Error("On-device speech recognition is not in this build");
    }
    const { ExpoSpeechRecognitionModule } = mod;

    const subscriptions = [
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        mod.addSpeechRecognitionListener("result", (event: any) => {
            const transcript = event?.results?.[0]?.transcript ?? "";
            if (transcript) {
                callbacks.onResult(transcript, Boolean(event?.isFinal));
            }
        }),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        mod.addSpeechRecognitionListener("error", (event: any) => {
            if (event?.error === "no-speech") {
                return;
            }
            callbacks.onError(String(event?.message ?? event?.error ?? "speech recognition failed"));
        }),
        mod.addSpeechRecognitionListener("end", () => callbacks.onEnd())
    ];

    ExpoSpeechRecognitionModule.start({
        lang: "en-US",
        interimResults: true,
        continuous: true,
        // The whole point of this variation: keep the patient's voice on the
        // phone. If the device cannot do it, we would rather know than silently
        // stream a patient's medication list to a recognition service.
        requiresOnDeviceRecognition: true,
        addsPunctuation: true,
        contextualStrings: options.contextualStrings ?? []
    });

    const cleanup = () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        for (const sub of subscriptions as any[]) {
            sub?.remove?.();
        }
    };

    return {
        stop: () => {
            ExpoSpeechRecognitionModule.stop();
            cleanup();
        },
        abort: () => {
            ExpoSpeechRecognitionModule.abort();
            cleanup();
        }
    };
}
